import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { IndustryDto as IndustryDtoCrm } from '@/services/ws-crm/src/main/resources/rest-api';
import type { PostIndustryDto } from '@/services/ws-post-crm/src/main/resources/rest-api';
import type { IndustryDto as IndustryDtoSelfBooking } from '@/services/ws-selfbooking/src/main/resources/rest-api';

import { LanguageKey } from 'src/types';

export type LocalizableLabel = 'nameEn' | 'nameFr' | 'nameIt' | 'nameDe';

const isLocalizableLanguageKey = (languageCode: string): languageCode is LanguageKey =>
  Object.values(LanguageKey).includes(languageCode as LanguageKey);

const getLocalizableLabel = (languageCode: string): LocalizableLabel => {
  const safeLanguageCode = isLocalizableLanguageKey(languageCode) ? languageCode : LanguageKey.English;
  const capitalizedCode = (safeLanguageCode.charAt(0).toUpperCase() + safeLanguageCode.slice(1)) as Capitalize<LanguageKey>;
  return `name${capitalizedCode}` as const;
};

export const formatIndustryOptionLabel = (industry: IndustryDtoCrm | IndustryDtoSelfBooking | PostIndustryDto, language: string) => {
  if (language === LanguageKey.German) {
    return industry.nameDe ?? '';
  }
  if (language === LanguageKey.French) {
    return industry.nameFr ?? '';
  }
  if (language === LanguageKey.Italian) {
    return industry.nameIt ?? '';
  }
  return industry.nameEn ?? '';
};

export const useIndustryLocalizedOptions = () => {
  const [, { language }] = useTranslation();
  // Here we use useCallback to provide stable function identity for memoization.
  // eslint-disable-next-line no-restricted-syntax
  const formatOptionLabel = useCallback(
    (option: { label: string; value: IndustryDtoCrm | IndustryDtoSelfBooking | PostIndustryDto }) =>
      formatIndustryOptionLabel(option.value, language),
    [language],
  );
  return useMemo(() => ({ formatOptionLabel, localizedLabel: getLocalizableLabel(language) }), [formatOptionLabel, language]);
};
